import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Loader2, Truck, CheckCircle2 } from "lucide-react";

const EMPTY_FORM = {
  full_name: "",
  email: "",
  phone: "",
  street_address: "",
  city: "",
  state: "NY",
  zip_code: "",
  gallons: 150,
  fuel_type: "standard",
  delivery_window: "standard",
  notes: "",
};

export default function OrderForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/orders", { ...form, gallons: Number(form.gallons) });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setSubmitted(false);

  return (
    <section id="order" className="relative py-24 md:py-32" data-testid="order-section"
      style={{ background: "linear-gradient(180deg, #B7D5EB 0%, #DCE9F3 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="relative inline-block mb-6">
            <div className="bg-brand-red text-white px-6 py-3 border-2 border-brand-navy shadow-[6px_6px_0_#0A1128] -rotate-1">
              <div className="font-heading text-2xl sm:text-3xl tracking-tight" style={{ textShadow: "2px 2px 0 #0A1128" }}>
                REQUEST A <span className="text-brand-gold">QUOTE</span>
              </div>
            </div>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl leading-[1.05] mb-6 text-brand-navy" data-testid="order-heading">
            Tell us what you need.<br/><span className="text-brand-red">We'll call you back.</span>
          </h2>
          <p className="text-brand-navy/80 leading-relaxed mb-8">
            Fill out the form and our dispatcher will reach out with a competitive quote within 2 hours during business hours. No payment, no commitment — just a price and a delivery window.
          </p>

          <div className="bg-white border-2 border-brand-navy p-6 shadow-[6px_6px_0_#D00017]" data-testid="order-info-card">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">What happens next</div>
            <ol className="space-y-2.5 text-sm text-brand-navy">
              <li className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-brand-red text-white font-heading text-xs flex items-center justify-center border-2 border-brand-navy">1</span>You submit the form below</li>
              <li className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-brand-red text-white font-heading text-xs flex items-center justify-center border-2 border-brand-navy">2</span>A real dispatcher calls you with today's price</li>
              <li className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-brand-red text-white font-heading text-xs flex items-center justify-center border-2 border-brand-navy">3</span>We schedule the delivery and top off your tank</li>
              <li className="flex gap-3"><span className="shrink-0 w-6 h-6 bg-brand-red text-white font-heading text-xs flex items-center justify-center border-2 border-brand-navy">4</span>You pay the driver on delivery — cash or card</li>
            </ol>
          </div>
        </div>

        <form onSubmit={submit} className={`md:col-span-7 bg-white border-2 border-brand-navy p-6 md:p-10 space-y-6 shadow-[8px_8px_0_#D00017] ${submitted ? "hidden" : ""}`} data-testid="order-form">
          <Section label="1 · Contact">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name"><input required value={form.full_name} onChange={(e)=>set("full_name",e.target.value)} className={inpCls} data-testid="input-full-name" /></Field>
              <Field label="Phone"><input required type="tel" value={form.phone} onChange={(e)=>set("phone",e.target.value)} className={inpCls} data-testid="input-phone" /></Field>
              <Field label="Email" full><input required type="email" value={form.email} onChange={(e)=>set("email",e.target.value)} className={inpCls} data-testid="input-email" /></Field>
            </div>
          </Section>

          <Section label="2 · Delivery Address">
            <div className="grid sm:grid-cols-6 gap-4">
              <Field label="Street" className="sm:col-span-6"><input required value={form.street_address} onChange={(e)=>set("street_address",e.target.value)} className={inpCls} data-testid="input-street" /></Field>
              <Field label="City" className="sm:col-span-3"><input required value={form.city} onChange={(e)=>set("city",e.target.value)} className={inpCls} data-testid="input-city" /></Field>
              <Field label="State" className="sm:col-span-1"><input required value={form.state} onChange={(e)=>set("state",e.target.value)} className={inpCls} data-testid="input-state" /></Field>
              <Field label="ZIP" className="sm:col-span-2"><input required value={form.zip_code} onChange={(e)=>set("zip_code",e.target.value)} className={inpCls} data-testid="input-zip" /></Field>
            </div>
          </Section>

          <Section label="3 · Oil & Delivery">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Approximate gallons needed">
                <input required type="number" min={1} value={form.gallons} onChange={(e)=>set("gallons", e.target.value)} className={inpCls} data-testid="input-gallons" />
              </Field>
              <Field label="Fuel type">
                <select value={form.fuel_type} onChange={(e)=>set("fuel_type", e.target.value)} className={inpCls} data-testid="select-fuel-type">
                  <option value="standard">Standard #2 Heating Oil</option>
                  <option value="premium">Premium with additive</option>
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-4" data-testid="delivery-window-group">
              {[
                { id: "standard", label: "Standard", sub: "1–2 business days" },
                { id: "next_day", label: "Next Day", sub: "Priority" },
                { id: "emergency", label: "Emergency", sub: "ASAP · 24/7" },
              ].map((o) => (
                <button type="button" key={o.id} onClick={()=>set("delivery_window", o.id)}
                  className={`p-4 text-left border transition-colors ${form.delivery_window===o.id?"border-brand-red bg-brand-red/5":"border-border hover:border-brand-red/50"}`}
                  data-testid={`delivery-window-${o.id}`}>
                  <div className="font-heading text-lg">{o.label}</div>
                  <div className="text-xs text-muted-foreground">{o.sub}</div>
                </button>
              ))}
            </div>
          </Section>

          <Section label="4 · Notes (optional)">
            <textarea value={form.notes} onChange={(e)=>set("notes", e.target.value)} rows={3} placeholder="Gate code, tank location, pets, anything else we should know" className={inpCls} data-testid="input-notes" />
          </Section>

          <button disabled={loading} type="submit" className="w-full bg-brand-red hover:bg-brand-redDark text-white font-heading tracking-wide py-4 text-lg inline-flex items-center justify-center gap-2 border-2 border-brand-navy shadow-[5px_5px_0_#0A1128] hover:shadow-[8px_8px_0_#0A1128] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all" data-testid="order-submit-btn">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Truck size={18} />}
            Request My Quote
          </button>
        </form>

        {submitted && (
          <div
            className="md:col-span-7 bg-white border-2 border-brand-navy p-8 md:p-14 shadow-[8px_8px_0_#D00017] flex flex-col items-center text-center"
            data-testid="order-success-message"
          >
            <div className="w-20 h-20 rounded-full bg-brand-red border-4 border-brand-navy flex items-center justify-center mb-6 shadow-[5px_5px_0_#0A1128]">
              <CheckCircle2 size={42} className="text-white" strokeWidth={2.25} />
            </div>
            <div className="relative inline-block mb-6">
              <div className="bg-brand-gold text-brand-navy px-5 py-2 border-2 border-brand-navy font-heading tracking-[0.15em] text-xs">
                REQUEST RECEIVED
              </div>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl text-brand-navy leading-tight mb-4" data-testid="order-thank-you-heading">
              Thank you for your request!
            </h3>
            <p className="text-brand-navy/80 text-lg leading-relaxed max-w-md mb-8">
              We've received your quote and a member of the <span className="font-bold text-brand-red">McFly's Fuel</span> team will contact you shortly.
            </p>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 bg-white hover:bg-brand-gold text-brand-navy px-6 py-3 font-heading tracking-wide border-2 border-brand-navy transition-colors"
              data-testid="order-submit-another"
            >
              Submit another request
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

const inpCls = "w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors";
const Field = ({ label, children, className = "", full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""} ${className}`}>
    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">{label}</div>
    {children}
  </label>
);
const Section = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-brand-red font-bold mb-3">{label}</div>
    {children}
  </div>
);
